import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  detectMobilePlatform,
  hasAppScreen,
  isIOSSafari,
  isInsideAppWebView,
  openInApp,
} from '../../utils/mobileApp/openInApp';

/**
 * OpenInAppBanner
 * ---------------
 * A dismissible strip at the bottom of the viewport, on phones only, offering to
 * continue in the ShowPhaze mobile app. Tapping it hands off via the `showphaze://`
 * scheme (Android goes through `intent://`, which the OS resolves) and falls back
 * to the store — see `utils/mobileApp/openInApp.ts` for the mechanics and the
 * open gap on the app side.
 *
 * Renders nothing on desktop, nothing inside a React Native WebView, nothing on the
 * routes in SUPPRESSED_PATHS, nothing once dismissed — and nothing in iOS Safari,
 * where the native Smart App Banner declared in `index.html` does the job better.
 * When the current page has a matching screen in the app (today: the shareable SOW /
 * PO links, and only for the contractor the document belongs to) the copy says so;
 * otherwise it just offers the app.
 *
 * Deliberately dependency-free (no MUI), like SandboxBanner: it mounts outside the
 * ThemeProvider-driven layout and has to look right on every route, authed or not.
 *
 * It is position:fixed and outside the node EventSOW / CoordinatorPO clone for
 * html2canvas, so it cannot leak into a generated PDF.
 */

const DISMISS_KEY = 'sp_open_in_app_dismissed_at';
const DISMISS_DAYS = 7;

/**
 * Routes where offering the app actively hurts.
 *
 * `/login` — a phone user who opens a SOW link while signed out is sent here by
 * RequireAuthForShareableLink. Tapping "Open" there would abandon the login they
 * were two taps from finishing, and leave the pending `emailRedirection` sitting in
 * localStorage. `/qr-data` — `useQrAppPrompt` (App.jsx) already pops its own
 * "Download App?" dialog there; two prompts for the same thing is worse than one.
 * `/show-media` — the page has its own "Open in the app" button (and Android already
 * tried the app before rendering it).
 */
const SUPPRESSED_PATHS = ['/login', '/qr-data', '/contractor-invite', '/show-media'];

/**
 * Height the banner occupies, reserved as page padding so it never sits on top of
 * whatever is last in the flow — the "Save and Send"/"Accept PO" buttons at the
 * bottom of EventSOW and CoordinatorPO are the ones that matter.
 */
const RESERVED_SPACE = '84px';

function wasDismissedRecently(): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const at = Number(raw);
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    // Private mode / storage disabled: just show the banner.
    return false;
  }
}

function rememberDismissal(): void {
  try {
    window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Nothing to do — the banner simply comes back next time.
  }
}

export default function OpenInAppBanner() {
  const location = useLocation();
  const currentUserId = useSelector(
    (state: { auth?: { userId?: string | null } }) => state?.auth?.userId,
  );

  const [platform] = useState(detectMobilePlatform);
  const [insideApp] = useState(isInsideAppWebView);
  const [nativeBannerHandles] = useState(isIOSSafari);
  const [dismissed, setDismissed] = useState(wasDismissedRecently);

  const pathname = location?.pathname ?? '';
  const search = location?.search ?? '';

  const visible =
    Boolean(platform) &&
    !insideApp &&
    !nativeBannerHandles &&
    !dismissed &&
    !SUPPRESSED_PATHS.includes(pathname);

  // Reserve the space instead of floating over the page. Written straight onto
  // <body> because the banner lives outside the app's layout tree and there is no
  // single scroll container to hang it off.
  useEffect(() => {
    if (!visible) return undefined;
    const previous = document.body.style.paddingBottom;
    document.body.style.paddingBottom = RESERVED_SPACE;
    return () => {
      document.body.style.paddingBottom = previous;
    };
  }, [visible]);

  if (!visible || !platform) return null;

  const deepLinked = hasAppScreen(pathname, search, currentUserId);

  const onDismiss = () => {
    rememberDismissal();
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label="Open in the ShowPhaze app"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        // Below MUI's modal layer (1300) on purpose: dialogs, the mobile MenuPopup
        // and the BulkSelectionBar all paint above this and stay tappable.
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 10px 10px 14px',
        borderRadius: 14,
        background: 'rgba(20, 20, 24, 0.96)',
        border: '1px solid rgba(255, 255, 255, 0.14)',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
        color: '#fff',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif',
      }}
    >
      <span
        style={{
          flex: '1 1 auto',
          fontSize: 13,
          lineHeight: 1.35,
          minWidth: 0,
        }}
      >
        {deepLinked ? 'Open this document in the ShowPhaze app' : 'Continue in the ShowPhaze app'}
      </span>

      <button
        type="button"
        onClick={() => openInApp({ pathname, search, platform, currentUserId })}
        style={{
          flex: '0 0 auto',
          padding: '8px 14px',
          border: 'none',
          borderRadius: 999,
          cursor: 'pointer',
          background: '#e10098',
          color: '#fff',
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: 0.2,
        }}
      >
        Open
      </button>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        title="Dismiss"
        style={{
          flex: '0 0 auto',
          width: 26,
          height: 26,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
          border: 'none',
          borderRadius: '50%',
          cursor: 'pointer',
          background: 'rgba(255, 255, 255, 0.12)',
          color: '#fff',
          fontSize: 16,
          lineHeight: 1,
          fontWeight: 700,
        }}
      >
        ×
      </button>
    </div>
  );
}
